import * as Storage from '../../storage';
import {ContactActionBox} from '../common/contact-action-box.comp';
import { submitTicket, uploadBase64File } from '../../api';
import { BaseWidgetProps } from '../../widget-container';
import * as React from 'react';
import * as ans from '@wix/answers-api';
import { ContactForm } from '@wix/answers-common-components';
import {EventType} from '../../../common/logger/event-type';
import { closeWidget, contentToHtml, WidgetResolvedTicketSettings } from '../../../common/utils';
import { fedopsInteractions } from '../../../common/fedops-interactions';
import { NavigationState } from '../../../common/navigator';
import { WidgetMessage, WidgetMessageType } from '../../../common/message-types';

export type TicketSuccess = any;

export type ContactPageProps = {
	baseConfig: BaseWidgetProps
};

export type ContactPageState = {
	submitedTicket: TicketSuccess | null;
	moduleSettings: WidgetResolvedTicketSettings | null;
	initialFieldData: ans.ContactFormInitialFieldData;
};

const emptyInitialFieldData: ans.ContactFormInitialFieldData = {
	fullname: '',
	email: '',
	title: '',
	description: '',
	customFields: {},
	attachments: []
};

export class ContactPage extends React.Component<ContactPageProps, ContactPageState> {
	state: ContactPageState = {
		submitedTicket: null,
		moduleSettings: null,
		initialFieldData: {
			...emptyInitialFieldData,
			email: this.props.baseConfig.messageHistory.getUserEmail(),
			fullname: this.props.baseConfig.messageHistory.getUserFirstName()
		}
	};

	contactLogger: ans.ContactLogger = {
		submit: (data) => this.logEvent(EventType.SUBMIT_TICKET_ATTEMPT, data),
		validationError: (data, invalidFields) => {
			this.logEvent(EventType.SUBMIT_TICKET_VALIDATION_ERROR, {...data, invalidFields});
		},
		submitSuccess: (data, ticketId) => this.logEvent(EventType.SUBMIT_TICKET_SUCCESS, {...data, ticketId}),
		submitFailure: (data) => this.logEvent(EventType.SUBMIT_TICKET_FAIL, data),
		cancel: (data) => this.logEvent(EventType.SUBMIT_TICKET_CANCEL, data),
		suggestedArticleSelected: (data, article) => {
			this.logEvent(EventType.SUBMIT_TICKET_SELECT_SUGGESTED_ARTICLE, {...data, article});
		}
	};

	logEvent = (type: EventType, data: any) => this.props.baseConfig.logger.logEvent(type, data);

	componentDidMount () {
		this.props.baseConfig.logger.logEvent(EventType.CONTACT_VIEW, {});
		if (window) {
			window.addEventListener('message', this.handleSettingsPostMessage.bind(this));
		}
	}

	componentWillUnmount () {
		if (window) {
			window.removeEventListener('message', this.handleSettingsPostMessage.bind(this));
		}
	}

	componentWillMount () {
		this.setState({ moduleSettings: this.props.baseConfig.widgetConfig.ticketModuleSettings });
	}

	handleSettingsPostMessage (event: any) {
		const newSettings: any = event.data.previewNewSettings;
		if (newSettings) {
			const moduleSettings = newSettings.ticketModule || this.state.moduleSettings;
			this.setState({ moduleSettings });
		}

		const widgetMessage: WidgetMessage = event.data.__ansWidget;
		if (widgetMessage && widgetMessage.type === WidgetMessageType.IDENTIFY_USER) {
			const {name, email} = widgetMessage.data.userData;
			this.setState({initialFieldData: {
				...this.state.initialFieldData,
				email,
				fullname: name
			}});
		}
	}

	submitTicket = (submitData: ans.ContactFormSubmitData): Promise<any> => {
		this.props.baseConfig.getFedopsLogger().interactionStarted(fedopsInteractions.submitTicket);
		const htmlContent = contentToHtml(submitData.description);

		const {attachments} = submitData;
		const content = htmlContent;

		const alteredSubmitData = {
			subject: submitData.title,
			customFields: submitData.customFieldsValuesMap,
			captcha: submitData.captchaToken,
			content,
			useInvisible: true,
			private: true,
			userFirstName: submitData.firstName,
			userLastName: submitData.lastName,
			userEmail: submitData.email,
			locale: this.props.baseConfig.locale,
			sourceType: ans.SourceType.WIDGET,
			sourceId: this.props.baseConfig.widgetId,
			labelIds: Storage.getLabelsToAddOnNextSubmit(),
			attachments
		};

		return submitTicket(this.props.baseConfig.apiUrl, alteredSubmitData)
		.then((ticket: TicketSuccess) => {
			this.props.baseConfig.getFedopsLogger().interactionEnded(fedopsInteractions.submitTicket);
			this.setState({submitedTicket: ticket});
			Storage.removeStoredContactField();
			Storage.removeStoreContactFlowArticleId();
			Storage.resetLabelsToAddOnNextSubmit();
		})
		.catch(() => {
			this.props.baseConfig.getFedopsLogger().interactionEnded(fedopsInteractions.submitTicket);
		});
	}

	onAttachmentUpload = (file: any, captchaToken?: string, progressCb?: (precentage: number) => void) => {
		return uploadBase64File(this.props.baseConfig.apiUrl, file, captchaToken, progressCb)
			.then((data: any) => ({name: data.filename, url: data.filelink}));
	}

	onGetSuggestedArticles: ans.onGetSuggestedArticlesProps =
		(title: string, description: string, customFields: ans.CustomFieldNameToValueMap): Promise<ans.Article[]> => {
			// tslint:disable-next-line:no-console
			console.log(description, customFields);
			const baseConfig = this.props.baseConfig;
			return baseConfig.kbApi.suggestionsSearch(title)
				.then((articles) => articles as ans.Article[]);
	}

	onSuggestedArticleClick = (article: ans.Article, currentFormData: ans.ContactFormInitialFieldData) => {
		Storage.storeContactFlowArticleId(article.id);
		Storage.storeContactField(currentFormData);
		this.props.baseConfig.widgetNavigator.goToState(NavigationState.article, article.id);
	}

	render () {
		const translate = this.props.baseConfig.translate;
		const moduleSettings = this.state.moduleSettings || this.props.baseConfig.widgetConfig.ticketModuleSettings;
		const contactMethod = moduleSettings.contactMethod;
		const responseTimeMessage = moduleSettings.responseTimeMessage ?
				moduleSettings.responseTimeMessage : translate('widget-ng.contact.response-time-message');
		const isInternal = contactMethod.type === ans.ContactMethodType.INTERNAL;
		const isExternal = contactMethod.type === ans.ContactMethodType.EXTERNAL;

		if (isInternal) {
			const contactTitle = moduleSettings.title ? moduleSettings.title : translate('widget-ng.contact.main.title');
			const contactSubTitle = moduleSettings.subTitle ? moduleSettings.subTitle : translate('widget-ng.contact.sub.title');
			const initialFieldData = Storage.getStoredContactField() || this.state.initialFieldData;
			const customFields: ans.ResolvedCustomField[] = contactMethod.resolvedFormFields || [];

			const reCaptchaData: ans.ContactFormCaptchaData = {
				isRequired: false,
				position: 'inline',
				key: this.props.baseConfig.widgetConfig.publicReCaptchaKey
			};

			const mainSubmittedContent = (
				<ContactActionBox
					baseConfig={this.props.baseConfig}
					doneBtnText={translate('widget-ng.contact.submitted.btn')}
					clickBtn={closeWidget}
				>
					<h2 className='tickets-title'>{translate('widget-ng.contact.submitted.main.title')}</h2>
					<div className='tickets-sub-title'>
						{translate('widget-ng.contact.submitted.sub.title')}
					</div>
					<div className='ticket-more-text'>{responseTimeMessage}</div>
				</ContactActionBox>
			);

			const mainContactForm = (
				<div className='contact-form-page'>
					<div className='contact-header'>
						<h2 className='contact-title'>{contactTitle}</h2>
						<div className='contact-description'>
							{contactSubTitle}
						</div>
					</div>
					<ContactForm
						translate={translate}
						initialFieldData={initialFieldData}
						customFields={customFields}
						onGetSuggestedArticles={this.onGetSuggestedArticles}
						reCaptchaData={reCaptchaData}
						onAttachmentUpload={this.onAttachmentUpload}
						onSubmit={this.submitTicket}
						onSuggestedArticleClick={this.onSuggestedArticleClick}
						logger={this.contactLogger}
						locale={this.props.baseConfig.locale}
						primaryLocale={this.props.baseConfig.widgetConfig.tenant.primaryLocale}
					/>
				</div>
			);

			return (
				<div className='contact-form-page'>
					{this.state.submitedTicket === null ? mainContactForm : mainSubmittedContent}
				</div>
			);
		} else if (isExternal) {
			const linkData = {
				link : this.props.baseConfig.widgetConfig.ticketModuleSettings.contactMethod.externalUrl || ''
			};

			return (
				<div className='contact-form-page'>
					<ContactActionBox
						baseConfig={this.props.baseConfig}
						doneBtnText={translate('widget-ng.contact-external.btn')}
						linkData={linkData}
					>
						<h2 className='tickets-title'>{translate('widget-ng.contact-external.title')}</h2>
						<div className='tickets-sub-title'>
							{translate('widget-ng.contact-external.sub.title')}
						</div>
						<div className='ticket-more-text'>{responseTimeMessage}</div>
					</ContactActionBox>
				</div>
			);
		} else {
			const mailLink = this.props.baseConfig.widgetConfig.ticketModuleSettings.contactMethod.email || '';
			const linkData = {
				link: mailLink ? `mailto:${mailLink}` : '',
				target: '_self'
			};

			return (
				<div className='contact-form-page'>
					<ContactActionBox
						baseConfig={this.props.baseConfig}
						doneBtnText={translate('widget-ng.contact-mail.btn')}
						linkData={linkData}
					>
						<h2 className='tickets-title'>{translate('widget-ng.contact-mail.title')}</h2>
						<div className='tickets-sub-title'>
							{translate('widget-ng.contact-mail.sub.title')}
						</div>
						<div className='ticket-more-text'>{responseTimeMessage}</div>
					</ContactActionBox>
				</div>
			);
		}
	}
}
